import React from "react";

export const CanvasContext = React.createContext<ICanvasContext>({});

export interface ICanvasData {
  component?: string;
  id?: string;
  position?: { top: number; left: number };
  dimension?: { width: string; height: string };
  /** Optional layout for mobile view (independent from desktop/web). */
  mobilePosition?: { top: number; left: number };
  mobileDimension?: { width: string; height: string };
  content?: string;
  type: string;
  isBackground?: boolean;
  /** Saved zoom level for SLIDE (Leaflet map). */
  slideZoom?: number;
  /** Saved camera orbit for 3D model (model-viewer), e.g. "0deg 75deg 2.5m". */
  cameraOrbit?: string;
  /** Optional serialized annotations for SLIDE (pins, lines, circles, etc.). */
  slideAnnotations?: any;
  /** How an image fills its box. Imported slides use "fill" so the frame matches the source. */
  imageFit?: "contain" | "cover" | "fill";
  /** Clockwise degrees. Imported PowerPoint shapes keep their rotation. */
  rotation?: number;
}

export interface ICanvasComponent {
  position?: { top: number; left: number };
  dimension?: { width: string; height: string };
  mobilePosition?: { top: number; left: number };
  mobileDimension?: { width: string; height: string };
  content?: string;
  id?: string;
  type: string;
  isReadOnly?: boolean;
  isBackground?: boolean;
  isPlayMode?: boolean;
  slideZoom?: number;
  cameraOrbit?: string;
  slideAnnotations?: any;
  imageFit?: "contain" | "cover" | "fill";
  rotation?: number;
}

export interface UploadEntity {
  entity_type: string;
  entity_id: string;
}

export interface ICanvasContext {
  state?: {
    canvasData: ICanvasData[];
    activeSelection: Set<string>;
    enableQuillToolbar: boolean;
  };
  actions?: {
    setCanvasData: React.Dispatch<React.SetStateAction<ICanvasData[]>>;
    setActiveSelection: React.Dispatch<React.SetStateAction<Set<string>>>;
    updateCanvasData: (data: Partial<ICanvasComponent>) => void;
    addElement: (type: string, overrides?: Partial<ICanvasData>) => void;
    setBackgroundImage: (content: string) => void;
    deleteElementById: (elementId: string) => void;
    setEnableQuillToolbar: (state: boolean) => void;
    /** Call after an action completes to persist current canvas (once per action, not on every tick). */
    saveNow: () => void;
    /** Return the latest in-memory canvas data snapshot. */
    getCanvasData: () => ICanvasData[];
    /** Start a grouped undo step for drag/resize interactions. */
    beginHistoryStep: () => void;
    /** Finish a grouped undo step. */
    endHistoryStep: () => void;
    /** Restore the previous canvas snapshot. */
    undo: () => void;
    /** Re-apply the last undone canvas snapshot. */
    redo: () => void;
    /** Move element one step up (forward) or down (backward) in layer order. */
    moveElementLayer: (elementId: string, direction: "up" | "down") => void;
    /** Duplicate an element with a slight offset. */
    duplicateElement?: (elementId: string) => void;
    /** Move element to the top of the layer stack. */
    bringElementToFront?: (elementId: string) => void;
    /** Move element to the bottom of the layer stack. */
    sendElementToBack?: (elementId: string) => void;
  };
  uploadEntity?: UploadEntity;
  /** When true, image/video elements only allow picking from uploads panel (no direct upload or URL). */
  uploadsOnlyFromLibrary?: boolean;
}
